import { BIG_PARTY, DEEP_CRYPT, DUNGEON, type NightmareType } from '@somnhold/content';
import { cellAtWorld, cellId, cellQ, cellR, findPath, touchGrid } from '../grid.ts';
import { DIRS, hexDist, hexRing, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng, type RngState } from '../math/rng.ts';
import type { BattleState, DeepCrypt, DungeonLayout, DungeonPuzzle, DungeonRoom, DungeonState, Hero, PuzzleKind } from '../types.ts';
import { placeCryptTraps, placeEruption } from './ascuas.ts';
import { damageHero, ev } from './combat.ts';

/*
 * The deep crypt (level III, docs/NIVELES.md): three floors — stone, embers, obsidian — each with a puzzle
 * room and a guardian. After a guardian falls, its hall opens the stairs down and the way out; the party
 * picks by standing together on one of them. Puzzles: braziers, twin levers, runes, the glass bridge.
 */

const P = DEEP_CRYPT;
const standing = (h: Hero): boolean => h.downTicks === 0;
const at = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
const ringOf = (room: DungeonRoom, k: number): Hex[] => hexRing(k).map((h) => ({ q: room.q + h.q, r: room.r + h.r }));

/** The edge cell of a room nearest to a point outside it. */
const edgeToward = (room: DungeonRoom, to: Hex): Hex => {
  let best = { q: room.q, r: room.r };
  let bd = 0x7fffffff;
  for (const h of ringOf(room, room.radius)) {
    const d = hexDist(h, to);
    if (d < bd) {
      bd = d;
      best = h;
    }
  }
  return best;
};

/** Spots spread evenly around a ring of the room, from a random start. */
const spread = (rng: RngState, room: DungeonRoom, ring: number, n: number): Hex[] => {
  const cells = ringOf(room, ring);
  const off = rngInt(rng, cells.length);
  return Array.from({ length: n }, (_, k) => cells[(off + Math.trunc((k * cells.length) / n)) % cells.length] as Hex);
};

const shuffle = (rng: RngState, n: number): number[] => {
  const o = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    [o[i], o[j]] = [o[j] as number, o[i] as number];
  }
  return o;
};

/** The glass bridge's safe path: from the tile by the door in to the tile by the door out, wandering a little. */
const glassPath = (rng: RngState, room: DungeonRoom, from: Hex, to: Hex): Hex[] => {
  const path: Hex[] = [from];
  const used = new Set([`${from.q},${from.r}`]);
  let cur = from;
  let guard = 0;
  while ((cur.q !== to.q || cur.r !== to.r) && guard++ < 40) {
    const d0 = hexDist(cur, to);
    const opts: { h: Hex; w: number }[] = [];
    for (const dir of DIRS) {
      const h = { q: cur.q + dir.q, r: cur.r + dir.r };
      if (used.has(`${h.q},${h.r}`) || hexDist(h, room) > room.radius - (hexDist(h, to) === 0 ? 0 : 1)) continue;
      const d = hexDist(h, to);
      // Closer always; sideways sometimes (never twice in a row against the end), never farther.
      if (d < d0) opts.push({ h, w: 3 });
      else if (d === d0 && path.length > 1 && guard % 2 === 1) opts.push({ h, w: 2 });
    }
    if (!opts.length) {
      for (const dir of DIRS) {
        const h = { q: cur.q + dir.q, r: cur.r + dir.r };
        if (hexDist(h, to) < d0) opts.push({ h, w: 1 });
      }
    }
    const pick = rngWeighted(rng, opts.map((o) => [o, o.w] as const));
    cur = pick.h;
    used.add(`${cur.q},${cur.r}`);
    path.push(cur);
  }
  return path;
};

export const makePuzzle = (rng: RngState, L: DungeonLayout, i: number, kind: PuzzleKind, heroes: number): DungeonPuzzle => {
  const room = L.rooms[i] as DungeonRoom;
  const base: DungeonPuzzle = { room: i, kind, solved: false, fails: 0, spots: [], lit: [], step: 0, hold: 0, need: 0, showFrom: 0, showUntil: 0, on: [], pressAt: 0 };
  if (kind === 'braziers') {
    const n = imin(6, P.braziers.count + (heroes > 2 ? heroes - 2 : 0));
    return { ...base, spots: spread(rng, room, room.radius - 1, n), lit: new Array<number>(n).fill(0), heat: new Array<number>(n).fill(0), need: P.braziers.lightTicks };
  }
  if (kind === 'levers') {
    const ring = ringOf(room, room.radius - 1);
    const off = rngInt(rng, ring.length);
    const half = Math.trunc(ring.length / 2);
    const q = Math.trunc(ring.length / 4);
    const spots: Hex[] = [];
    for (let k = 0; k < P.levers.rounds; k++) spots.push(ring[(off + k * q) % ring.length] as Hex, ring[(off + k * q + half) % ring.length] as Hex);
    return { ...base, spots, need: P.levers.holdTicks };
  }
  if (kind === 'runes') return { ...base, spots: spread(rng, room, room.radius - 1, P.runes.plates), lit: shuffle(rng, P.runes.plates) };
  const din = L.doors[i - 1] as Hex;
  const dout = L.doors[i] as Hex;
  return { ...base, spots: glassPath(rng, room, edgeToward(room, din), edgeToward(room, dout)) };
};

/** Lays the deep crypt out at battle creation: its puzzles (drawn for this run), the embers of its second floor, and the descent. */
export const placeDeep = (s: BattleState, guardianHp: number[]): void => {
  const L = s.map.dungeon;
  const d = s.dungeon;
  if (!L || !d) return;
  const rng = seedRng(deriveSeed(s.seed, 4245));
  L.rooms.forEach((r, i) => {
    if (r.kind === 'entry') d.cleared[i] = true;
  });
  d.puzzles = [];
  L.rooms.forEach((r, i) => {
    if (r.puzzle) d.puzzles?.push(makePuzzle(rng, L, i, r.puzzle, s.heroes.length || 1));
  });
  // Lava and vents on the floor of embers, never on a puzzle's plates, bowls or bridge.
  placeCryptTraps(s, (room) => room.floor === 1 && room.puzzle !== 'glass', false, d.puzzles.flatMap((p) => p.spots));
  d.deep = { floor: 0, floors: P.floors, cleared: 0, stairs: null, exit: null, stairsT: 0, exitT: 0, left: false, guardian: 0, hp: guardianHp };
};

/** Draws this run's three puzzles: all different (no twin levers for a hero alone: they take two). */
export const drawPuzzles = (rng: RngState, heroes = 2): PuzzleKind[] => {
  const all: PuzzleKind[] = heroes > 1 ? ['braziers', 'levers', 'runes', 'glass'] : ['braziers', 'runes', 'glass'];
  return shuffle(rng, all.length).slice(0, P.floors).map((k) => all[k] as PuzzleKind);
};

/** The puzzle of a room (deep crypt). */
export const puzzleOf = (d: DungeonState, room: number): DungeonPuzzle | undefined => d.puzzles?.find((p) => p.room === room);

/** A room's way out is blocked by its unsolved puzzle. */
export const puzzleHolds = (d: DungeonState, room: number): boolean => !!puzzleOf(d, room) && !(puzzleOf(d, room) as DungeonPuzzle).solved;

/**
 * A hero's walk on an unsolved glass bridge, as cells (from where it stands to the tapped one, the tapped one twice):
 * tile center to tile center, since a straight line from wherever it stands could clip the glass next door, and over
 * the tiles the group has already reached when that gets there (they show lit to everyone). Null off the bridge.
 */
export const glassRoute = (s: BattleState, from: number, to: number): number[] | null => {
  const d = s.dungeon;
  const L = s.map.dungeon;
  if (!d || !L || d.room < 0 || from < 0 || to < 0 || from === to) return null;
  const p = puzzleOf(d, d.room);
  const room = L.rooms[d.room];
  if (!p || p.kind !== 'glass' || p.solved || !room) return null;
  const R = s.map.radius;
  const hex = (id: number): Hex => ({ q: cellQ(R, id), r: cellR(R, id) });
  if (hexDist(hex(from), room) > room.radius && hexDist(hex(to), room) > room.radius) return null;
  // Breadth-first over the reached tiles (and the tapped one), in path order then the fixed directions.
  const known = new Set(p.spots.slice(0, p.step + 1).map((h) => cellId(R, h.q, h.r)));
  known.add(to);
  const parent = new Map<number, number>([[from, from]]);
  const queue = known.has(from) ? [from] : [];
  for (let i = 0; i < queue.length && !parent.has(to); i++) {
    const cur = queue[i] as number;
    const c = hex(cur);
    for (const dir of DIRS) {
      const n = cellId(R, c.q + dir.q, c.r + dir.r);
      if (!known.has(n) || parent.has(n)) continue;
      parent.set(n, cur);
      queue.push(n);
    }
  }
  let route: number[];
  if (parent.has(to)) {
    route = [];
    for (let c = to; c !== from; c = parent.get(c) as number) route.unshift(c);
  } else route = findPath(s, from, to);
  return route.length ? [from, ...route, to] : null;
};

/** Where the party is pulled in when a room wakes: onto the glass bridge's first tile, never onto the glass. */
export const pullSpot = (d: DungeonState, room: number): Hex | null => {
  const p = puzzleOf(d, room);
  return p && p.kind === 'glass' ? (p.spots[0] as Hex) : null;
};

/** When a puzzle room wakes. */
export const wakePuzzle = (s: BattleState, d: DungeonState, room: number): void => {
  const p = puzzleOf(d, room);
  if (!p) return;
  p.pressAt = s.tick + P.pressEvery;
  if (p.kind === 'runes') show(p, s.tick + 18, P.runes.showTicks * p.spots.length);
  if (p.kind === 'glass') show(p, s.tick + 6, P.glass.showTicks);
};

const show = (p: DungeonPuzzle, from: number, len: number): void => {
  p.showFrom = from;
  p.showUntil = from + len;
  if (p.kind === 'runes') p.step = 0;
};

/** A few more nightmares come in: on a timer while the puzzle stands, and at once on every mistake. */
const reinforce = (s: BattleState, d: DungeonState, p: DungeonPuzzle, spawn: SpawnFn): void => {
  const L = s.map.dungeon as DungeonLayout;
  const room = L.rooms[p.room] as DungeonRoom;
  const floor = room.floor ?? 0;
  const beasts = p.kind === 'glass' ? P.flyers : (P.bestiary[floor] ?? {});
  const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
  // Past four heroes the press grows by fewer bodies: they are tougher instead (the crypt's enemyHpBp, BIG_PARTY).
  const n = s.heroes.length || 1;
  let left = P.pressPoints * imin(n, BIG_PARTY.measured) + (n > BIG_PARTY.measured ? mulBp(P.pressPoints * (n - BIG_PARTY.measured), BIG_PARTY.countBp) : 0);
  const spots = ringOf(room, room.radius - 1);
  let guard = 0;
  while (left > 0 && guard++ < 20) {
    const kind = rngWeighted(s.rng, entries);
    left -= kind === 'gargoyle' || kind === 'wraith' ? 4 : 3;
    const h = spots[rngInt(s.rng, spots.length)] as Hex;
    const w = at(h);
    spawn(kind, rngChance(s.rng, P.inkBp[floor] ?? 0) ? 2 : 1, w.x, w.y);
  }
  ev(s, { t: 'skill', hero: 0, skill: 'puzzlePress', x: at(room).x, y: at(room).y });
  void d;
};

export type SpawnFn = (kind: NightmareType, tier: 1 | 2, x: number, y: number) => void;

const mistake = (s: BattleState, d: DungeonState, p: DungeonPuzzle, spawn: SpawnFn): void => {
  p.fails++;
  reinforce(s, d, p, spawn);
  p.pressAt = s.tick + P.pressEvery;
};

const solve = (s: BattleState, p: DungeonPuzzle, room: DungeonRoom): void => {
  p.solved = true;
  p.pressAt = 0;
  touchGrid(s);
  ev(s, { t: 'boom', x: at(room).x, y: at(room).y, r: room.radius * 1000, kind: 'puzzleSolved' });
};

/** Who must all be there for the party to move on: the ones still playing; a dropped player's hero just stands there and is taken along. */
const deciders = (s: BattleState): Hero[] => {
  const up = s.heroes.filter(standing);
  const live = up.filter((h) => h.control !== 'ghost');
  return live.length > 0 ? live : up;
};

/** One tick of the puzzle in the room being fought. */
export const puzzleTick = (s: BattleState, d: DungeonState, spawn: SpawnFn): void => {
  if (d.room < 0) return;
  const p = puzzleOf(d, d.room);
  const L = s.map.dungeon;
  if (!p || p.solved || !L) return;
  const room = L.rooms[p.room] as DungeonRoom;
  const up = s.heroes.filter(standing);
  const near = (h: Hex, r: number): Hero[] => up.filter((o) => withinRange(o.x, o.y, at(h).x, at(h).y, r));
  if (p.pressAt > 0 && s.tick >= p.pressAt) {
    reinforce(s, d, p, spawn);
    p.pressAt = s.tick + P.pressEvery;
  }
  if (p.kind === 'braziers') {
    const heat = p.heat ?? (p.heat = new Array<number>(p.spots.length).fill(0));
    p.on = [];
    p.spots.forEach((h, i) => {
      const by = near(h, P.braziers.range).length > 0;
      if (by) p.on.push(i);
      if ((p.lit[i] ?? 0) > s.tick) return;
      if ((p.lit[i] ?? 0) > 0 && (p.lit[i] ?? 0) === s.tick) ev(s, { t: 'boom', x: at(h).x, y: at(h).y, r: 400, kind: 'brazierOut' });
      heat[i] = by ? (heat[i] ?? 0) + 1 : 0;
      if ((heat[i] ?? 0) >= p.need) {
        heat[i] = 0;
        p.lit[i] = s.tick + P.braziers.burnTicks;
        ev(s, { t: 'boom', x: at(h).x, y: at(h).y, r: 500, kind: 'brazier' });
      }
    });
    if (p.lit.every((t) => t > s.tick)) solve(s, p, room);
    return;
  }
  if (p.kind === 'levers') {
    p.on = [];
    p.spots.forEach((h, i) => {
      if (near(h, P.levers.range).length) p.on.push(i);
    });
    const a = p.step * 2;
    const both = p.on.includes(a) && p.on.includes(a + 1);
    p.hold = both ? p.hold + 1 : p.hold > 2 ? p.hold - 2 : 0;
    if (p.hold >= p.need) {
      p.hold = 0;
      p.step++;
      const c = at(p.spots[a] as Hex);
      ev(s, { t: 'boom', x: c.x, y: c.y, r: 600, kind: 'lever' });
      if (p.step * 2 >= p.spots.length) solve(s, p, room);
    }
    return;
  }
  if (p.kind === 'runes') {
    const on: number[] = [];
    p.spots.forEach((h, i) => {
      if (near(h, P.runes.plateRange).length) on.push(i);
    });
    const fresh = on.filter((i) => !p.on.includes(i));
    p.on = on;
    if (s.tick < p.showUntil) return;
    const next = p.lit[p.step] as number;
    const done = p.lit.slice(0, p.step);
    const wrong = fresh.find((i) => i !== next && !done.includes(i));
    if (wrong !== undefined) {
      for (const h of p.spots) placeEruption(s, at(h).x, at(h).y, 18);
      show(p, s.tick + P.runes.replayDelay, P.runes.showTicks * p.spots.length);
      mistake(s, d, p, spawn);
      return;
    }
    if (!on.includes(next)) return;
    p.step++;
    const c = at(p.spots[next] as Hex);
    ev(s, { t: 'boom', x: c.x, y: c.y, r: P.runes.plateRange, kind: 'rune' });
    if (p.step >= p.spots.length) solve(s, p, room);
    return;
  }
  // The glass bridge.
  const safe = new Set(p.spots.map((h) => cellId(s.map.radius, h.q, h.r)));
  const start = p.spots[0] as Hex;
  const end = p.spots[p.spots.length - 1] as Hex;
  const onStart = near(start, P.glass.range).length > 0;
  for (const h of up) {
    const hx = worldToHex(h.x, h.y);
    if (hexDist(hx, room) > room.radius) continue;
    const cell = cellAtWorld(s, h.x, h.y);
    if (safe.has(cell)) {
      const k = p.spots.findIndex((x) => x.q === hx.q && x.r === hx.r);
      if (k > p.step) p.step = k;
      continue;
    }
    // Off the path: the glass gives, the hero drops back to the start.
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'glassFall' });
    damageHero(s, h, P.glass.fallDamage);
    const c = at(start);
    h.x = h.destX = h.ax = c.x;
    h.y = h.destY = h.ay = c.y;
    h.moving = false;
    h.path = [];
    mistake(s, d, p, spawn);
  }
  // Stepping onto the first tile again shows the path again.
  const was = p.on.includes(0);
  p.on = onStart ? [0] : [];
  if (onStart && !was && s.tick >= p.showUntil) show(p, s.tick + 2, P.glass.showTicks);
  const across = deciders(s);
  if (across.length && across.every((h) => withinRange(h.x, h.y, at(end).x, at(end).y, P.glass.range + 300))) solve(s, p, room);
};

/**
 * A guardian's hall is cleared: the floor counts, and (but on the last floor) the stairs down and the way
 * out open on either side of the hall.
 */
export const openDescent = (s: BattleState, d: DungeonState): void => {
  const dp = d.deep;
  const L = s.map.dungeon;
  if (!dp || !L) return;
  dp.cleared = dp.floor + 1;
  dp.guardian = 0;
  if (dp.floor >= dp.floors - 1) return;
  const hall = L.rooms.find((r) => r.kind === 'boss' && r.floor === dp.floor);
  if (!hall) return;
  const ring = ringOf(hall, 2);
  dp.stairs = ring[0] as Hex;
  dp.exit = ring[Math.trunc(ring.length / 2)] as Hex;
  const c = at(dp.stairs);
  ev(s, { t: 'skill', hero: 0, skill: 'stairsOpen', x: c.x, y: c.y });
};

/** Each tick: the party standing together on the stairs goes down; on the way out, it leaves. */
export const descentTick = (s: BattleState, d: DungeonState): void => {
  const dp = d.deep;
  if (!dp || !dp.stairs || !dp.exit) return;
  const up = deciders(s);
  // Everyone standing there on purpose: walking through on the way to the other one does not count.
  const all = (h: Hex): boolean => up.length > 0 && up.every((o) => !o.moving && withinRange(o.x, o.y, at(h).x, at(h).y, P.stairsRange));
  dp.stairsT = all(dp.stairs) ? dp.stairsT + 1 : 0;
  dp.exitT = all(dp.exit) ? dp.exitT + 1 : 0;
  if (dp.exitT >= P.chooseTicks) {
    dp.left = true;
    return;
  }
  if (dp.stairsT >= P.chooseTicks) descend(s, d, dp);
};

const descend = (s: BattleState, d: DungeonState, dp: DeepCrypt): void => {
  const L = s.map.dungeon as DungeonLayout;
  dp.floor++;
  dp.stairs = null;
  dp.exit = null;
  dp.stairsT = 0;
  dp.exitT = 0;
  d.boss = 0;
  d.marks = [];
  d.pools = [];
  s.zones = s.zones.filter((z) => z.kind === 'lava' || z.kind === 'vent');
  const entry = L.rooms.find((r) => r.kind === 'entry' && r.floor === dp.floor);
  if (!entry) return;
  const c = at(entry);
  s.heroes.forEach((h, k) => {
    if (h.downTicks > 0) {
      h.downTicks = 0;
      h.revive = 0;
      h.hp = imax(1, mulBp(h.stats.maxHp, DUNGEON.clearReviveHpBp));
      h.anim = 'idle';
      ev(s, { t: 'up', id: h.id });
    }
    h.x = h.destX = h.ax = c.x + ((k % 3) - 1) * 700;
    h.y = h.destY = h.ay = c.y + (k > 2 ? 600 : 0);
    h.moving = false;
    h.path = [];
    h.target = 0;
  });
  touchGrid(s);
  ev(s, { t: 'skill', hero: 0, skill: 'descend', x: c.x, y: c.y });
};

/** The guardian of a floor: the Warden on stone, the magma lord in embers, the colossus on the glass. */
export const guardianOf = (floor: number): 'warden' | 'magmaLord' | 'colossus' => (floor === 0 ? 'warden' : floor === 1 ? 'magmaLord' : 'colossus');
